// frontend/src/components/UI/CurrencySelector.jsx
import React, { useState, useRef, useEffect } from "react";
import { useCurrency } from "../../context/CurrencyContext";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faCheck } from "@fortawesome/free-solid-svg-icons";

const CURRENCY_OPTIONS = [
  { code: "SLL", label: "Sierra Leone Leone", symbol: "Le" },
  { code: "USD", label: "US Dollar", symbol: "$" },
  { code: "MYR", label: "Malaysian Ringgit", symbol: "RM" },
];

const CurrencySelector = () => {
  const { currency, setCurrency, getExchangeRate } = useCurrency();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentOption = CURRENCY_OPTIONS.find((c) => c.code === currency);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (code) => {
    setCurrency(code);
    setIsOpen(false);
  };

  return (
    <div className="currency-selector" ref={dropdownRef}>
      <button
        className="currency-selector-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Select currency"
      >
        <span className="currency-symbol">{currentOption?.symbol}</span>
        <span className="currency-code">{currentOption?.code}</span>
        <FontAwesomeIcon
          icon={faChevronDown}
          className={`dropdown-icon ${isOpen ? "open" : ""}`}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="currency-dropdown"
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
          >
            {CURRENCY_OPTIONS.map((option) => (
              <button
                key={option.code}
                className={`currency-option ${currency === option.code ? "active" : ""}`}
                onClick={() => handleSelect(option.code)}
              >
                <span className="currency-option-symbol">{option.symbol}</span>
                <span className="currency-option-label">{option.label}</span>
                {currency === option.code && (
                  <FontAwesomeIcon
                    icon={faCheck}
                    className="currency-option-check"
                  />
                )}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        .currency-selector {
          position: relative;
        }

        .currency-selector-btn {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.3rem 0.6rem;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 6px;
          color: #ffffff;
          cursor: pointer;
          font-size: 0.75rem;
          font-weight: 500;
          transition: all 0.3s ease;
          font-family: "Inter", sans-serif;
        }

        .currency-selector-btn:hover {
          background: rgba(255, 255, 255, 0.2);
        }

        .currency-symbol {
          font-weight: 600;
        }

        .currency-code {
          font-weight: 400;
        }

        .dropdown-icon {
          font-size: 0.55rem;
          transition: transform 0.3s ease;
        }

        .dropdown-icon.open {
          transform: rotate(180deg);
        }

        .currency-dropdown {
          position: absolute;
          top: calc(100% + 4px);
          right: 0;
          min-width: 220px;
          background: #ffffff;
          border-radius: 8px;
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
          border: 1px solid #f0f0f0;
          padding: 0.25rem;
          z-index: 1000;
        }

        .currency-option {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0.75rem;
          width: 100%;
          background: none;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.3s ease;
          font-family: "Inter", sans-serif;
          font-size: 0.813rem;
          color: #1F1F1F;
          text-align: left;
        }

        .currency-option:hover {
          background: #F8F8F8;
        }

        .currency-option.active {
          background: #e8f5e9;
          color: #006B2E;
        }

        .currency-option-symbol {
          font-weight: 600;
          width: 28px;
        }

        .currency-option-label {
          flex: 1;
        }

        .currency-option-check {
          color: #D9A321;
          font-size: 0.75rem;
        }

        @media (max-width: 768px) {
          .currency-selector-btn {
            padding: 0.25rem 0.4rem;
            font-size: 0.65rem;
          }
          .currency-dropdown {
            min-width: 180px;
            right: -10px;
          }
          .currency-option {
            font-size: 0.75rem;
            padding: 0.4rem 0.6rem;
          }
        }
      `}</style>
    </div>
  );
};

export default CurrencySelector;
